import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// Input replaced with native inputs for OTP boxes to allow fixed widths
import Button from '../common/Button/index'
import Typography from '../common/Typography/index'
import Card from '../common/Card/index'
import { sendVerifyEmail, confirmVerifyEmail, resetEmailVerification } from '../../state/redux/actions/userActions'

const AdminEmailVerification = ({ onSuccess, onCancel, targetEmail = null, autoSend = false }) => {
  const dispatch = useDispatch()
  const { emailVerification } = useSelector(state => state.user)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [timeLeft, setTimeLeft] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const inputsRef = useRef([])

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [timeLeft])

  // Format time display
  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Auto-submit logic
  useEffect(() => {
    if (code.every(digit => digit !== '')) {
      const submitTimer = setTimeout(() => {
        handleVerification()
      }, 500)
      return () => clearTimeout(submitTimer)
    }
    return undefined
  }, [code])

  // Handle verification success
  useEffect(() => {
    if (emailVerification.verified) {
      onSuccess?.()
    }
  }, [emailVerification.verified, onSuccess])

  // Auto-send when modal opens and autoSend option is enabled
  useEffect(() => {
    if (autoSend && !emailVerification.otpSent) {
      handleSendCode()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend, targetEmail])

  // Handle input change
  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value

    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  // Handle key down events
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const inputValue = e.target.value

      if (inputValue === '') {
        e.preventDefault()
        if (index > 0) {
          const newCode = [...code]
          newCode[index - 1] = ''
          setCode(newCode)
          inputsRef.current[index - 1].focus()
        }
      } else {
        e.target.value = ''
        const newCode = [...code]
        newCode[index] = ''
        setCode(newCode)
      }
    }
  }

  // Handle verification
  const handleVerification = async () => {
    const verificationCode = code.join('')
    if (verificationCode.length !== 6) {
      return
    }

    try {
      const payload = targetEmail ? { otp: verificationCode, email: targetEmail } : { otp: verificationCode }
      await dispatch(confirmVerifyEmail(payload)).unwrap()
    } catch (error) {
      // Clear the code inputs on error
      setCode(['', '', '', '', '', ''])
      inputsRef.current[0]?.focus()
    }
  }

  // Handle resend code
  const handleResendCode = async () => {
    setIsResending(true)
    try {
  await dispatch(sendVerifyEmail({ email: targetEmail })).unwrap()
  setTimeLeft(600) // Reset timer (10 minutes)
      setCode(['', '', '', '', '', '']) // Clear inputs
      inputsRef.current[0]?.focus()
    } catch (error) {
      // Error is handled by Redux
    } finally {
      setIsResending(false)
    }
  }

  // Handle send initial code
  const handleSendCode = async () => {
    try {
      await dispatch(sendVerifyEmail({ email: targetEmail })).unwrap()
  setTimeLeft(600) // Start timer (10 minutes)
    } catch (error) {
      // Error is handled by Redux
    }
  }

  // Handle cancel
  const handleCancel = () => {
    dispatch(resetEmailVerification())
    onCancel?.()
  }

  // Prevent background scrolling and attach Escape key listener while modal is open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleCancel()
    }

    // Lock body scroll
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = originalOverflow
      document.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Manual verification trigger
  const handleManualVerify = e => {
    e.preventDefault()
    handleVerification()
  }

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleCancel()
      }}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleCancel}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md px-4">
        <Card className="flex p-8 justify-center items-center w-full">
          <div className="w-full">
            <div className="flex justify-between items-start mb-4">
              <div>
                <Typography variant="h3" className="py-2">
                  Verify Email
                </Typography>
                <Typography variant="body" className="text-gray-600">
                  {!emailVerification.otpSent
                    ? "Click 'Send Code' to receive a verification code via email"
                    : "Enter the 6-digit code sent to your email"
                  }
                </Typography>
              </div>

              <button
                aria-label="Close verification modal"
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700 ml-4"
              >
                ×
              </button>
            </div>

            {/* Error Display */}
            {emailVerification.error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                <Typography variant="body">{emailVerification.error}</Typography>
              </div>
            )}

            {/* Success Display */}
            {emailVerification.verified && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                <Typography variant="body">Email verified successfully!</Typography>
              </div>
            )}

            {!emailVerification.otpSent ? (
              // Send Code Phase
              <div className="space-y-4">
                <Button
                  onClick={handleSendCode}
                  disabled={emailVerification.loading}
                  className="w-full"
                >
                  {emailVerification.loading ? 'Sending...' : 'Send Verification Code'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              // Verification Phase
              <form onSubmit={handleManualVerify}>
                {/* Code Input Fields */}
                <div className="flex gap-3 justify-center items-center mb-6 flex-nowrap">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => (inputsRef.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleChange(index, e.target.value)}
                      onKeyDown={e => handleKeyDown(index, e)}
                      disabled={emailVerification.loading}
                      aria-label={`Verification digit ${index + 1}`}
                      className={`w-12 h-12 text-center border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:ring-0 appearance-none p-0
                                [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden
                                ${emailVerification.loading ? 'opacity-50 cursor-not-allowed' : ''}
                                ${emailVerification.error ? 'border-red-500' : ''}
                                ${emailVerification.verified ? 'border-green-500' : ''}`}
                    />
                  ))}
                </div>

                {/* Timer and Resend */}
                <div className="flex justify-between items-center mb-6">
                  <Typography variant="body" className="text-gray-600">
                    {timeLeft > 0 ? `Expires in: ${formatTime(timeLeft)}` : 'Code expired'}
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    variant="link"
                    className="shadow-none border-collapse text-teal-600"
                    onClick={handleResendCode}
                    disabled={isResending || timeLeft > 0}
                  >
                    {isResending ? 'Sending...' : 'Resend'}
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={emailVerification.loading || code.some(digit => digit === '')}
                  >
                    {emailVerification.loading ? 'Verifying...' : 'Verify'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}

export default AdminEmailVerification